import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  ArrowUp,
  Brain,
  Check,
  ChevronDown,
  FileText,
  Loader2,
  Plus,
  Shield,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatChatAttachmentSize } from '@/modules/chat/application/chat-attachment-file-types';
import {
  CHAT_PERMISSION_MODE_DISPLAY_ORDER,
  CHAT_PERMISSION_MODE_STORAGE_KEY,
  readStoredChatPermissionMode,
} from '@/modules/chat/application/chat-permission';
import { ChatPermissionMode, ChatReasoningEffort } from '@/modules/common/types';

export type PromptBoxAttachmentStatus = 'uploading' | 'uploaded' | 'error';

export interface PromptBoxAttachment {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  status: PromptBoxAttachmentStatus;
  errorMessage?: string;
}

export interface PromptBoxProps {
  onSubmit?: (
    message: string,
    reasoningEffort: ChatReasoningEffort,
    permissionMode: ChatPermissionMode
  ) => void;
  attachments?: PromptBoxAttachment[];
  fileInputAccept?: string;
  onFilesSelected?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  submitDisabled?: boolean;
  className?: string;
}

const CHAT_REASONING_EFFORT_STORAGE_KEY = 'mavue.chat.reasoningEffort';
const DEFAULT_PROMPTBOX_PLACEHOLDER = 'What do you want to know?';
const CHAT_PROMPT_PLACEHOLDER_KEYS = [
  'modules.mavue2.chat.promptPlaceholders.products',
  'modules.mavue2.chat.promptPlaceholders.documents',
  'modules.mavue2.chat.promptPlaceholders.supplier',
  'modules.mavue2.chat.promptPlaceholders.report',
  'modules.mavue2.chat.promptPlaceholders.tasks',
  'modules.mavue2.chat.promptPlaceholders.reportGaps',
] as const;
const CHAT_REASONING_EFFORT_OPTIONS = [
  ChatReasoningEffort.NONE,
  ChatReasoningEffort.LOW,
  ChatReasoningEffort.MEDIUM,
  ChatReasoningEffort.HIGH,
  ChatReasoningEffort.XHIGH,
] as const;
const CHAT_PERMISSION_MODE_ICONS: Record<ChatPermissionMode, typeof Shield> = {
  [ChatPermissionMode.DEFAULT_PERMISSIONS]: Shield,
  [ChatPermissionMode.AUTO_REVIEW]: ShieldCheck,
  [ChatPermissionMode.FULL_ACCESS]: ShieldAlert,
};
const CLIPBOARD_IMAGE_EXTENSIONS_BY_TYPE: Record<string, string> = {
  'image/gif': '.gif',
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
};

function isChatReasoningEffort(value: unknown): value is ChatReasoningEffort {
  return CHAT_REASONING_EFFORT_OPTIONS.some((option) => option === value);
}

function readStoredReasoningEffort() {
  if (typeof window === 'undefined') {
    return ChatReasoningEffort.NONE;
  }

  const storedValue = window.localStorage.getItem(CHAT_REASONING_EFFORT_STORAGE_KEY);

  if (isChatReasoningEffort(storedValue)) {
    return storedValue;
  }

  if (storedValue) {
    window.localStorage.removeItem(CHAT_REASONING_EFFORT_STORAGE_KEY);
  }

  return ChatReasoningEffort.NONE;
}

function hasFileExtension(fileName: string) {
  return /\.[A-Za-z0-9]+$/.test(fileName);
}

function normalizeClipboardImageFile(file: File, index: number) {
  const extension = CLIPBOARD_IMAGE_EXTENSIONS_BY_TYPE[file.type.toLowerCase()];

  if (!extension || (file.name && hasFileExtension(file.name))) {
    return file;
  }

  return new File([file], `screenshot-${Date.now()}-${index + 1}${extension}`, {
    lastModified: Date.now(),
    type: file.type,
  });
}

export const PromptBox = React.forwardRef<HTMLTextAreaElement, PromptBoxProps>(
  (
    {
      onSubmit,
      placeholder = DEFAULT_PROMPTBOX_PLACEHOLDER,
      attachments = [],
      fileInputAccept,
      onFilesSelected,
      onRemoveAttachment,
      disabled = false,
      loading = false,
      submitDisabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [value, setValue] = React.useState('');
    const [placeholderIndex, setPlaceholderIndex] = React.useState(0);
    const [reasoningEffort, setReasoningEffort] =
      React.useState<ChatReasoningEffort>(readStoredReasoningEffort);
    const [permissionMode, setPermissionMode] = React.useState<ChatPermissionMode>(
      readStoredChatPermissionMode
    );
    const internalTextareaRef = React.useRef<HTMLTextAreaElement>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const hasValue = value.trim().length > 0;
    const canSubmit = hasValue && !disabled && !submitDisabled;
    const hasUploadingAttachment = attachments.some(
      (attachment) => attachment.status === 'uploading'
    );
    const usesDefaultPlaceholder = placeholder === DEFAULT_PROMPTBOX_PLACEHOLDER;
    const shouldAnimatePlaceholder = usesDefaultPlaceholder && value.length === 0;
    const currentPlaceholderKey =
      CHAT_PROMPT_PLACEHOLDER_KEYS[placeholderIndex] ?? CHAT_PROMPT_PLACEHOLDER_KEYS[0];
    const currentPlaceholder = usesDefaultPlaceholder ? t(currentPlaceholderKey) : placeholder;
    const selectedReasoningEffort =
      CHAT_REASONING_EFFORT_OPTIONS.find((option) => option === reasoningEffort) ??
      CHAT_REASONING_EFFORT_OPTIONS[0];
    const selectedReasoningEffortLabel = t(
      `modules.mavue2.chat.reasoning.efforts.${selectedReasoningEffort}`
    );
    const selectedPermissionMode =
      CHAT_PERMISSION_MODE_DISPLAY_ORDER.find((option) => option === permissionMode) ??
      CHAT_PERMISSION_MODE_DISPLAY_ORDER[0];
    const selectedPermissionModeLabel = t(
      `modules.mavue2.chat.permissions.modes.${selectedPermissionMode}`
    );
    const SelectedPermissionModeIcon = CHAT_PERMISSION_MODE_ICONS[selectedPermissionMode];

    // Combine refs
    React.useImperativeHandle(ref, () => internalTextareaRef.current!, []);

    // Auto-resize textarea
    React.useLayoutEffect(() => {
      const textarea = internalTextareaRef.current;
      if (textarea) {
        textarea.style.height = 'auto';
        const newHeight = Math.min(textarea.scrollHeight, 200);
        textarea.style.height = `${newHeight}px`;
      }
    }, [value]);

    React.useEffect(() => {
      if (!shouldAnimatePlaceholder) {
        return;
      }

      const intervalId = window.setInterval(() => {
        setPlaceholderIndex((index) => (index + 1) % CHAT_PROMPT_PLACEHOLDER_KEYS.length);
      }, 3200);

      return () => window.clearInterval(intervalId);
    }, [shouldAnimatePlaceholder]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    };

    const handleSubmit = () => {
      if (canSubmit && onSubmit) {
        onSubmit(value.trim(), reasoningEffort, permissionMode);
        setValue('');
      }
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.currentTarget.files ?? []);

      if (files.length > 0) {
        onFilesSelected?.(files);
      }

      event.currentTarget.value = '';
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onFilesSelected) {
        return;
      }

      const files = Array.from(event.clipboardData.items)
        .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
        .filter((file): file is File => Boolean(file))
        .filter((file) => Boolean(CLIPBOARD_IMAGE_EXTENSIONS_BY_TYPE[file.type.toLowerCase()]))
        .map(normalizeClipboardImageFile);

      if (files.length === 0) {
        return;
      }

      event.preventDefault();
      onFilesSelected(files);
    };

    const handleReasoningEffortChange = (nextReasoningEffort: ChatReasoningEffort) => {
      setReasoningEffort(nextReasoningEffort);
      window.localStorage.setItem(CHAT_REASONING_EFFORT_STORAGE_KEY, nextReasoningEffort);
    };

    const handlePermissionModeChange = (nextPermissionMode: ChatPermissionMode) => {
      setPermissionMode(nextPermissionMode);
      window.localStorage.setItem(CHAT_PERMISSION_MODE_STORAGE_KEY, nextPermissionMode);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    return (
      <div
        className={cn(
          'flex flex-col rounded-[28px] p-2 shadow-sm transition-colors bg-white border dark:bg-[#303030] dark:border-transparent cursor-text',
          className
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={fileInputAccept}
          className="hidden"
          onChange={handleFileInputChange}
        />
        <div className="relative">
          <Textarea
            ref={internalTextareaRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={currentPlaceholder}
            disabled={disabled}
            rows={1}
            className={cn(
              'resize-none border-0 bg-transparent p-3 text-foreground dark:text-white placeholder:text-muted-foreground dark:placeholder:text-gray-300 focus:ring-0 focus-visible:outline-none focus-visible:ring-0 min-h-12 shadow-none',
              shouldAnimatePlaceholder &&
                'placeholder:text-transparent dark:placeholder:text-transparent'
            )}
            {...props}
          />

          <AnimatePresence mode="wait" initial={false}>
            {shouldAnimatePlaceholder && (
              <motion.span
                key={currentPlaceholder}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.18 }}
                className="pointer-events-none absolute left-3 right-3 top-3 line-clamp-2 text-base text-muted-foreground dark:text-gray-300 md:text-sm"
              >
                {currentPlaceholder}
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {attachments.length > 0 && (
          <div className="order-first flex flex-wrap gap-2 px-2 pb-2">
            {attachments.map((attachment) => {
              const isUploading = attachment.status === 'uploading';
              const isError = attachment.status === 'error';

              return (
                <div
                  key={attachment.id}
                  className={cn(
                    'flex min-w-0 max-w-full items-center gap-2 rounded-md border bg-muted/50 px-2 py-1 text-xs',
                    isError && 'border-red-200 bg-red-50 text-red-950'
                  )}
                >
                  {isUploading ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
                  ) : isError ? (
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600" />
                  ) : (
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 max-w-44 truncate" title={attachment.name}>
                    {attachment.name}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {formatChatAttachmentSize(attachment.sizeBytes)}
                  </span>
                  {attachment.errorMessage ? (
                    <span className="min-w-0 max-w-32 truncate text-red-700">
                      {attachment.errorMessage}
                    </span>
                  ) : null}
                  {onRemoveAttachment ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={isUploading || hasUploadingAttachment}
                      className="h-5 w-5 shrink-0 rounded-full"
                      onClick={() => onRemoveAttachment(attachment.id)}
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove attachment</span>
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-0.5 p-1 pt-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              {onFilesSelected ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled || loading}
                  aria-label="Attach files"
                  className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground dark:text-gray-300 dark:hover:text-white"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              ) : null}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || loading}
                    aria-label={t('modules.mavue2.chat.permissions.ariaLabel', {
                      mode: selectedPermissionModeLabel,
                    })}
                    className="h-8 max-w-full gap-1 rounded-full px-2 text-muted-foreground hover:text-foreground dark:text-gray-300 dark:hover:text-white"
                  >
                    <SelectedPermissionModeIcon className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate text-foreground dark:text-white">
                      {selectedPermissionModeLabel}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-56">
                  <DropdownMenuLabel className="text-muted-foreground">
                    {t('modules.mavue2.chat.permissions.label')}
                  </DropdownMenuLabel>
                  {CHAT_PERMISSION_MODE_DISPLAY_ORDER.map((option) => {
                    const OptionIcon = CHAT_PERMISSION_MODE_ICONS[option];

                    return (
                      <DropdownMenuItem
                        key={option}
                        onSelect={() => handlePermissionModeChange(option)}
                      >
                        <OptionIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span>{t(`modules.mavue2.chat.permissions.modes.${option}`)}</span>
                        {option === permissionMode && <Check className="ml-auto h-4 w-4" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled || loading}
                    aria-label={t('modules.mavue2.chat.reasoning.ariaLabel', {
                      effort: selectedReasoningEffortLabel,
                    })}
                    className="h-8 max-w-full gap-1 rounded-full px-2 text-muted-foreground hover:text-foreground dark:text-gray-300 dark:hover:text-white"
                  >
                    <Brain className="h-4 w-4 shrink-0" />
                    <span className="min-w-0 truncate text-foreground dark:text-white">
                      {selectedReasoningEffortLabel}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-48">
                  <DropdownMenuLabel className="text-muted-foreground">
                    {t('modules.mavue2.chat.reasoning.label')}
                  </DropdownMenuLabel>
                  {CHAT_REASONING_EFFORT_OPTIONS.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => handleReasoningEffortChange(option)}
                    >
                      <span>{t(`modules.mavue2.chat.reasoning.efforts.${option}`)}</span>
                      {option === reasoningEffort && <Check className="ml-auto h-4 w-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                type="button"
                size="icon"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="h-8 w-8 rounded-full bg-black text-white hover:bg-black/80 dark:bg-white dark:text-black dark:hover:bg-white/80 disabled:bg-black/40 dark:disabled:bg-[#515151]"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
                <span className="sr-only">{t('modules.mavue2.chat.sendMessage')}</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

PromptBox.displayName = 'PromptBox';
